import { Box3, Color, Object3D, Scene, Sphere, Vector3 } from "three";

import { generateUUID } from "three/src/math/MathUtils.js";
import { CameraInterface } from "../engine/Engine";

export class Point extends EventTarget {
  protected _point?: Vector3;
  protected _joints?: Point[] = [];

  protected _type = "Point";
  protected _name = "Point";

  protected _childs?: Point[] = [];
  protected _parent?: Point;
  protected _color2d?: Color;
  protected _color3d?: Color;
  protected _isDirty = true;
  protected _isVisible = false;
  protected _raycastable = false;
  protected _renderMode = "2d";
  protected _markedForDelete = false;

  constructor() {
    super();
    this._name = "Point_" + generateUUID();
    this._point = new Vector3(0, 0, 0);
    this._root = new Object3D();
  }

  protected _root?: Object3D;
  get root(): Object3D | undefined {
    if (!this._root) return undefined;
    this._root.name = "mesh_of_" + this.name;
    return this._root;
  }

  protected _collider?: Object3D;
  get collider(): Object3D | undefined {
    if (this._collider) this._collider.name = "collider_of_" + this.name;
    return this._collider;
  }

  get isConstruct() {
    return false;
  }

  set name(value: string) {
    this._name = value;
  }

  get name() {
    return this._name;
  }

  set color2d(color: Color) {
    this._color2d = color;
  }

  get color2d(): Color | undefined {
    return this._color2d;
  }

  set color3d(color: Color) {
    this._color3d = color;
  }

  get color3d(): Color | undefined {
    return this._color3d;
  }

  set isDirty(value: boolean) {
    this._isDirty = value;
  }

  get isDirty() {
    return this._isDirty;
  }

  get visible() {
    return this._isVisible;
  }

  set visible(value: boolean) {
    this._isVisible = value;
  }

  get point() {
    return this._point!;
  }

  get bound(): Box3 | Sphere | undefined {
    return undefined;
  }

  set point(value: Vector3) {
    this._isDirty = true;
    this._joints?.forEach((joint) => (joint.isDirty = true));
    this._point = value;
  }

  get joints() {
    return this._joints;
  }

  set joints(value) {
    this._joints = value;
  }

  get raycastable() {
    return this._raycastable;
  }

  set raycastable(value: boolean) {
    this._raycastable = value;
  }

  get raycastingTarget(): Object3D | undefined {
    return undefined;
  }

  get markedForDelete() {
    return this._markedForDelete;
  }

  set markedForDelete(value: boolean) {
    this._markedForDelete = value;
  }

  get type() {
    return this._type;
  }

  addJoint(point: Point) {
    if (!this._joints?.includes(point)) this._joints?.push(point);
  }

  removeJoint(point: Point) {
    this._joints = this._joints?.filter((p) => p != point);
  }

  addChild(point: Point) {
    if (!this._childs?.includes(point)) {
      this._childs?.push(point);
      point._parent = this;
      this._isDirty = true;
    }
  }

  removeChild(point: Point) {
    this._childs = this._childs?.filter((p) => p != point);
    point._parent = undefined;
  }

  removeFromParent() {
    this._parent?.removeChild(this);
  }

  get parent() {
    return this._parent;
  }

  bindJoint(point: Point) {
    this.addJoint(point);
    point.addJoint(this);
  }

  unbindJoint(point: Point) {
    this.removeJoint(point);
    point.removeJoint(this);
  }

  get childs() {
    return this._childs;
  }

  set renderMode(value: string) {
    this._isDirty = true;
    this._renderMode = value;
    this.childs?.forEach((c) => (c.renderMode = value));
  }

  isOfType(type: new (..._: any) => {}) {
    return this instanceof type;
  }

  isOfAnyType(...types: (new (..._: any) => {})[]) {
    return types.find((t) => this instanceof t);
  }

  countChildsOf(...classes: (new (..._: any) => {})[]): number {
    const found = this._joints?.filter((j) =>
      classes.find((c) => j instanceof c)
    );
    return found?.length || 0;
  }

  findFirstChildOf(...classes: (new (..._: any) => {})[]): Point | undefined {
    return this._joints?.find((j) => classes.find((c) => j instanceof c));
  }

  dispose(scene?: Scene) {
    this._joints?.forEach((joint) => {
      this.unbindJoint(joint);
    });
    this.markedForDelete = true;
  }

  private _attributes: any = {};
  get attributes() {
    return this._attributes as any;
  }

  setAttributes(name: string, value: any) {
    this._attributes[name] = value;
  }

  update(scene?: Scene, camera?: CameraInterface) {}

  render2d(scene?: Scene, camera?: CameraInterface) {}
  render3d(scene?: Scene, camera?: CameraInterface) {}

  render(scene?: Scene, camera?: CameraInterface) {
    if (this._renderMode == "3d") {
      this.render3d(scene, camera);
    } else if (this._renderMode == "2d") {
      this.render2d(scene, camera);
    }
    this.isDirty = false;
  }
}
